import PropTypes from 'prop-types'
import * as R from 'ramda'
import React from 'react'
import { Link, withRouter } from 'react-router-dom'
import { Form, Tab, Checkbox } from 'semantic-ui-react'

import { connectApi } from 'libs/connectApi'
import { mutate } from 'hooks/useAxios'
import { toast } from 'libs/toast'

import { setSurveyFromId } from 'actions'
import { confirmationMessagePanes } from 'components/Admin/Surveys/LandingPageSettings/confirmationMessagePanes'
import ExpirationSettings from 'components/ExpirationSettings'
import SurveyBrandDropdownContainer from 'components/SurveyBrandDropdown/container'
import SurveyLanguageDropdownContainer from 'components/SurveyLanguageDropdown/container'
import { TooltipLabel } from 'components/TooltipLabel'
import constants from 'config/constants'
import dayjs from 'dayjs'
import { ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import { getDayjsToday, getDefaultHoursSchedule, getWorkspaceTimezone } from 'libs/date'
import generateUserActivityLog from 'libs/generateUserActivityLog'
import { withSurveySettingContext } from 'pages/SurveySettings/context'
import ErrorText from 'shared/components/forms/ErrorText'
import { connect } from 'react-redux'
import {
  CTA_VALIDATION_ERRORS,
  getCallToActionButtonsError,
  hasInvalidTicketUrlPlaceholder,
  getCloseScheduleTimestamp,
  isCloseScheduleInThePast,
  normalizeCallToActionButtonLinks,
} from './utils'
import SurveyLanguageSetting from 'components/SurveyLanguageSetting'
import { getLanguageLabelByCode, getLanguageCodeByLabel } from 'organisms/FeedbackFilterGroup/utils'

export const SurveyConfigurationContext = React.createContext()

const TICKET_URL_PLACEHOLDER_ERROR =
  'The ticket URL is not valid. Use {{ ticket_id }} and {{ customer_id }} only.'

const DEFAULT_EXPIRATION_SETTINGS = [
  {
    type: 'post_email_sent',
    enabled: false,
    duration_value: 0,
    duration_unit: 'hours',
  },
]

export class SurveyConfigurationContainer extends React.Component {
  submitButtonRef = React.createRef()
  ticketUrlInputRef = React.createRef()
  static propTypes = {
    survey: PropTypes.object,
    updateSurvey: PropTypes.shape({
      callApi: PropTypes.func,
      loading: PropTypes.bool,
    }),
    context: PropTypes.object,
    surveyLanguages: PropTypes.object,
    surveyBrands: PropTypes.object,
    createUserActivityLog: PropTypes.object,
    enabledMultiligualSurvey: PropTypes.bool,
    enableCtaButtonVariables: PropTypes.bool,
    enableFileUploadQuestion: PropTypes.bool,
  }

  state = {
    confirmationMessage: {
      positive: '',
      neutral: '',
      negative: '',
    },
    callToActionButtons: {
      positive: {},
      neutral: {},
      negative: {},
    },
    activeConfirmationMessageTab: 'positive',
    isLoading: true,
    ticketUrlPrefix: '',
    ticketUrlPrefixError: '',
    languageId: 1,
    brandId: null,
    onlyShowCustomerQuestionIfCustomerUnknown: true,
    isCustomerQuestionRequireAndHideAnonymousResponse: false,
    isAnonymousFileUploadAllowed: false,
    isExpirationEnabled: false,
    timeUntilExpiration: 0,
    expirationUnit: 'hours',
    isClosedEnabled: false,
    scheduleDate: null,
    scheduleTime: null,
    expiration_settings: DEFAULT_EXPIRATION_SETTINGS,
    isSurveyLanguageUpdating: false,
  }

  componentDidMount = () => {
    const {
      confirmation_message,
      call_to_action_buttons,
      name,
      ticket_url_prefix,
      language,
      brand,
      only_show_customer_question_if_customer_unknown,
      is_customer_question_required,
      only_save_non_anonymous_ratings,
      is_anonymous_file_upload_allowed,
      is_expiration_enabled,
      time_until_expiration,
      expiration_unit,
      is_closed_enabled,
      closed_at,
      expiration_settings,
      survey_languages,
      is_multilingual_survey_enabled,
    } = this.props.survey.data

    const workspaceTimezone = getWorkspaceTimezone()
    const storedCloseSchedule =
      is_closed_enabled && closed_at ? dayjs.utc(closed_at).tz(workspaceTimezone) : null

    this.setState({
      confirmationMessage: confirmation_message,
      callToActionButtons: call_to_action_buttons,
      surveyName: name,
      ticketUrlPrefix: ticket_url_prefix,
      languageId: language,
      brandId: brand,
      onlyShowCustomerQuestionIfCustomerUnknown: only_show_customer_question_if_customer_unknown,
      isCustomerQuestionRequireAndHideAnonymousResponse:
        is_customer_question_required && only_save_non_anonymous_ratings,
      isAnonymousFileUploadAllowed: Boolean(is_anonymous_file_upload_allowed),
      isExpirationEnabled: is_expiration_enabled,
      timeUntilExpiration: time_until_expiration,
      expirationUnit: expiration_unit,
      isClosedEnabled: is_closed_enabled,
      scheduleDate: storedCloseSchedule || getDayjsToday(),
      scheduleTime: storedCloseSchedule ? storedCloseSchedule.hour() : getDefaultHoursSchedule(),
      expiration_settings:
        R.isNil(expiration_settings) || R.isEmpty(expiration_settings)
          ? DEFAULT_EXPIRATION_SETTINGS
          : expiration_settings,
      multiSurveyLanguages: survey_languages || [],
      lastSavedSurveyLanguages: survey_languages || [],
      isMultilingualSurveyChecked: is_multilingual_survey_enabled,
    })

    const surveySettingContext = this.props.context
    surveySettingContext.setContext({
      currentSaveFunction: this.save,
    })
  }

  componentDidUpdate(prevProps) {
    const surveySettingContext = this.props.context
    // Only toggle the global save button loading when this is a full configuration save
    if (
      prevProps.updateSurvey.loading !== this.props.updateSurvey.loading &&
      !this.state.isSurveyLanguageUpdating
    ) {
      surveySettingContext.setContext({
        isSubmitting: this.props.updateSurvey.loading,
      })
    }
  }

  setIsSurveyBuilderDirty(value) {
    const surveySettingContext = this.props.context

    surveySettingContext.setContext({
      isSurveyBuilderDirty: value,
    })
  }

  handleConfirmationMessageTabChange = (event, { activeIndex, panes }) => {
    this.setState({
      activeConfirmationMessageTab: panes[activeIndex].menuItem.key,
    })
    this.setIsSurveyBuilderDirty(true)
  }

  handleConfirmationMessageChange = (event) => {
    const newConfirmationMessage = this.state.confirmationMessage
    newConfirmationMessage[this.state.activeConfirmationMessageTab] = event.target.value

    this.setState({
      confirmationMessage: newConfirmationMessage,
    })
    this.setIsSurveyBuilderDirty(true)
  }

  handleSurveyName = (event) => {
    this.setState({ surveyName: event.target.value })
    this.setIsSurveyBuilderDirty(true)
  }

  handleTicketUrlPrefix = (event) => {
    const textValue = event.target.value
    this.setState((prev) =>
      prev.ticketUrlPrefixError
        ? { ticketUrlPrefix: textValue || '', ticketUrlPrefixError: '' }
        : { ticketUrlPrefix: textValue || '' }
    )
    this.setIsSurveyBuilderDirty(true)
  }

  onChangeSurveyLanguage = (newSurveyLanuage) => {
    this.setState({ multiSurveyLanguages: newSurveyLanuage })
    this.setIsSurveyBuilderDirty(true)
  }

  onChangeDefaultSurveyLanguage = (newSurveyLanuage) => {
    this.onChangeSurveyLanguage(newSurveyLanuage)

    const defaultLanguageCode = newSurveyLanuage.find((item) => item.is_default)?.language
    const languageLabel = getLanguageLabelByCode(defaultLanguageCode)
    this.handleSetLanguageByName(languageLabel)
  }

  onCallToActionSettingsChange = (type, settings) => {
    this.setState((prevState) => {
      return R.mergeDeepRight(prevState, { callToActionButtons: { [type]: settings } })
    })
    this.setIsSurveyBuilderDirty(true)
  }

  setLanguage = (languageId) => {
    this.setState({ languageId })
    this.setIsSurveyBuilderDirty(true)
  }

  handleSetLanguageByName = (languageName) => {
    const surveyLanguages = this.props.surveyLanguages?.data?.results
    if (!surveyLanguages || !languageName) {
      return
    }

    const normalizedLanguageName = languageName.toLowerCase()
    const matchedLanguage = surveyLanguages.find(
      (item) => item.name?.toLowerCase() === normalizedLanguageName
    )

    const languageId = matchedLanguage?.id ?? this.state.languageId
    this.setLanguage(languageId)
  }

  onChangeIsMultilingualSurveyChecked = (isMultilingualSurveyChecked) => {
    this.setState({ isMultilingualSurveyChecked: isMultilingualSurveyChecked })
    this.setIsSurveyBuilderDirty(true)

    // If unchecked → do nothing
    if (!isMultilingualSurveyChecked) {
      return
    }

    const language = this.findLanguageByLanguageId(this.state.languageId)
    this.updateNewDefaultWhenEnableMultilingual(getLanguageCodeByLabel(language.name))
  }

  updateNewDefaultWhenEnableMultilingual = (languageCode) => {
    const multiSurveyLanguages = this.state.lastSavedSurveyLanguages
    const existingLanguage = multiSurveyLanguages.find((item) => item.language === languageCode)

    if (existingLanguage) {
      const newSurveyLanguages = multiSurveyLanguages.map((lang) => ({
        ...lang,
        is_active: lang.language === languageCode ? true : lang.is_active,
        is_default: lang.language === languageCode,
        is_translated: lang.language === languageCode ? true : lang.is_translated,
      }))
      this.onChangeSurveyLanguage(newSurveyLanguages)
    } else {
      const newDefaultSurveyLanguage = {
        language: languageCode,
        is_active: true,
        is_default: true,
        is_translated: true,
      }
      const newSurveyLanguages = multiSurveyLanguages.map((lang) => ({
        ...lang,
        is_active: lang.is_active,
        is_default: false,
        is_translated: lang.is_translated,
      }))
      this.onChangeSurveyLanguage([newDefaultSurveyLanguage, ...newSurveyLanguages])
    }
  }

  findLanguageByLanguageId = (languageId) => {
    const surveyLanguages = this.props.surveyLanguages?.data?.results
    const matchedLanguage = surveyLanguages.find((item) => item.id == languageId)
    return matchedLanguage
  }

  onBrandChange = (brandId) => {
    this.setState({ brandId })
    this.setIsSurveyBuilderDirty(true)
  }

  updateExpirationSetting = (type, key, value) => {
    this.setState((prevState) => {
      return {
        ...prevState,
        expiration_settings: prevState?.expiration_settings.map((item) =>
          item.type === type ? { ...item, [key]: value } : item
        ),
      }
    })
    this.setIsSurveyBuilderDirty(true)
  }

  onToggleEnabled = (type, enabled) => {
    this.updateExpirationSetting(type, 'enabled', enabled)
  }

  onChangeIsClosedEnabled = (isClosedEnabled) => {
    this.setState((prevState) => {
      const needsFreshSchedule =
        isClosedEnabled && isCloseScheduleInThePast(prevState.scheduleDate, prevState.scheduleTime)

      return {
        isClosedEnabled,
        ...(needsFreshSchedule && {
          scheduleDate: getDayjsToday(),
          scheduleTime: getDefaultHoursSchedule(),
        }),
      }
    })
    this.setIsSurveyBuilderDirty(true)
  }

  onChangeDuration = (type, duration_value) => {
    this.updateExpirationSetting(type, 'duration_value', duration_value)
  }

  onChangeUnit = (type, duration_unit) => {
    this.updateExpirationSetting(type, 'duration_unit', duration_unit)
  }

  save = () => {
    this.submitButtonRef.current.click()
  }

  /**
   * Reverts survey languages to the last saved state and updates the language dropdown
   */
  revertToLastSavedLanguages = () => {
    this.setState({
      multiSurveyLanguages: this.state.lastSavedSurveyLanguages,
    })
    const defaultLanguageCode = this.state.lastSavedSurveyLanguages.find(
      (item) => item.is_default
    )?.language
    const languageLabel = getLanguageLabelByCode(defaultLanguageCode)
    this.handleSetLanguageByName(languageLabel)
  }

  /**
   * Saves only survey language table data (instant updates)
   */
  updateSurveyLanguages = async ({
    patchingConfigurationData = {},
    successMessage = 'Language updated successfully.',
    failureMessage = 'Oops, there was an error updating languages. Please try again.',
  }) => {
    const payload = patchingConfigurationData

    // Snapshot the current default language before the PATCH so we can detect a switch
    const prevDefaultLanguage = (this.props.survey.data.survey_languages || []).find(
      (l) => l.is_default
    )?.language

    // Separate loading state language table we don't show loading state for instant action
    this.setState({ isSurveyLanguageUpdating: true })

    this.props.updateSurvey
      .callApi({
        data: payload,
      })
      .then((response) => {
        toast.success(successMessage)
        this.props.dispatch(setSurveyFromId(response))
        mutate(
          `account/survey/${this.props.match.params.surveyId}/`,
          (current) =>
            current
              ? {
                  ...current,
                  ...response,
                  connector: current.connector,
                  brand_name: current.brand_name,
                }
              : response,
          false
        )
        this.saveUserActivityLog(payload, 200)
        this.setIsSurveyBuilderDirty(false)
        if (payload.survey_languages) {
          this.setState({ lastSavedSurveyLanguages: payload.survey_languages })
        }
        // Invalidate email caches when the default language changed so that
        // Publish → Emails immediately reflects the BE-seeded values
        if (payload.default_language && payload.default_language !== prevDefaultLanguage) {
          const surveyToken = response.token
          mutate(`survey/survey/${surveyToken}/email-content-settings/`)
          mutate(`survey/survey/${surveyToken}/email-content-translations/`)
        }
      })
      .catch((err) => {
        toast.error(failureMessage)
        this.saveUserActivityLog(payload, err.status)
        this.revertToLastSavedLanguages()
        throw err
      })
      .finally(() => {
        this.setState({ isSurveyLanguageUpdating: false })
      })
  }

  /**
   *
   * @param {object} requestBody
   * @param {number} statusCode
   */
  saveUserActivityLog = (requestBody, statusCode) => {
    const surveyId = this.props.match.params.surveyId
    const data = generateUserActivityLog({
      action: ACTION_NAME.edit_survey_configuration,
      target: `sur_${surveyId}`,
      statusCode,
      requestBody,
    })
    this.props.createUserActivityLog.callApi({ data })
  }

  /**
   * Updates survey configuration with optional patching data
   * @param {Object} params
   * @param {Object} params.patchingConfigurationData - Data to override/patch the configuration
   * @param {string} params.successMessage - Success message to show on successful update
   * @param {string} params.failureMessage - Failure message to show on failed update
   * @param {Function} params.onFailure
 - Callback function to execute on failure
   * @returns {Promise<void>}
   */
  updateSurveyConfiguration = async ({
    patchingConfigurationData = {},
    successMessage = 'Your survey has been updated.',
    failureMessage = 'Oops, there was an error saving your survey. Please try again.',
  }) => {
    const closedAtTimestamp = getCloseScheduleTimestamp(
      this.state.scheduleDate,
      this.state.scheduleTime
    )

    // D16 — the last SAVED config, so only buttons the admin actually touched are
    // validated. This is what stops a survey with a pre-existing invalid link from
    // blocking an unrelated rename: 92 surveys cannot save their Configure tab today
    // for exactly that reason.
    const previousButtons = R.pathOr({}, ['survey', 'data', 'call_to_action_buttons'], this.props)

    // TS5 — repairs a scheme-less plain link so the save succeeds (76 of the 143 bad
    // links in production). TS6 — never runs on a template: normalizeUrl would rewrite
    // '{{ticket.url}}' to 'https://{{ticket.url}}' and break the merge tag silently.
    const callToActionButtons = normalizeCallToActionButtonLinks(
      this.state.callToActionButtons,
      previousButtons
    )

    const storedTicketUrlPrefix =
      R.pathOr('', ['survey', 'data', 'ticket_url_prefix'], this.props) || ''
    const hasTicketUrlChanged = (this.state.ticketUrlPrefix || '') !== storedTicketUrlPrefix

    if (hasTicketUrlChanged && hasInvalidTicketUrlPlaceholder(this.state.ticketUrlPrefix)) {
      this.setState({ ticketUrlPrefixError: TICKET_URL_PLACEHOLDER_ERROR })
      this.ticketUrlInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      this.ticketUrlInputRef.current?.focus()
      return
    }

    if (this.state.ticketUrlPrefixError) {
      this.setState({ ticketUrlPrefixError: '' })
    }

    const callToActionError = getCallToActionButtonsError(callToActionButtons, {
      previousButtons,
      enableCtaButtonVariables: Boolean(this.props.enableCtaButtonVariables),
    })

    if (!callToActionError) {
      const configurationData = {
        confirmation_message: this.state.confirmationMessage,
        call_to_action_buttons: callToActionButtons,
        name: this.state.surveyName,
        ticket_url_prefix: this.state.ticketUrlPrefix,
        language: this.state.languageId,
        brand: this.state.brandId,
        only_show_customer_question_if_customer_unknown:
          this.state.onlyShowCustomerQuestionIfCustomerUnknown,
        is_customer_question_required: this.state.isCustomerQuestionRequireAndHideAnonymousResponse,
        only_save_non_anonymous_ratings:
          this.state.isCustomerQuestionRequireAndHideAnonymousResponse,
        is_anonymous_file_upload_allowed: this.state.isAnonymousFileUploadAllowed,
        is_expiration_enabled: this.state.isExpirationEnabled,
        time_until_expiration: this.state.timeUntilExpiration,
        expiration_unit: this.state.expirationUnit,
        is_closed_enabled: this.state.isClosedEnabled,
        ...(this.state.isClosedEnabled && { closed_at: closedAtTimestamp }),
        is_multilingual_survey_enabled: this.state.isMultilingualSurveyChecked,
        ...(this.state.isMultilingualSurveyChecked && {
          survey_languages: this.state.multiSurveyLanguages,
          default_language:
            this.state.multiSurveyLanguages.find((item) => item.is_default)?.language || '',
        }),
      }

      if (constants.emailSurveyTypes.includes(this.props.survey.data.type)) {
        configurationData['expiration_settings'] = this.state.expiration_settings
      }
      const overwrittenConfigurationData = { ...configurationData, ...patchingConfigurationData }
      this.props.updateSurvey
        .callApi({
          data: overwrittenConfigurationData,
        })
        .then((response) => {
          this.props.dispatch(setSurveyFromId(response))
          mutate(
            `account/survey/${this.props.match.params.surveyId}/`,
            (current) =>
              current
                ? {
                    ...current,
                    ...response,
                    connector: current.connector,
                    brand_name: current.brand_name,
                  }
                : response,
            false
          )
          toast.success(successMessage)
          this.saveUserActivityLog(overwrittenConfigurationData, 200)
          // Reflect what was actually STORED, not what was typed. normalizeUrl may
          // have repaired a scheme-less link, and leaving state on the typed value
          // makes the field disagree with storage until a reload — and makes the
          // next save's changed-only diff compare against a value the admin never
          // saw. Only after a successful PATCH, so a failure never rewrites input.
          this.setState({
            callToActionButtons,
            lastSavedSurveyLanguages: this.state.multiSurveyLanguages,
          })
          this.setIsSurveyBuilderDirty(false)
        })
        .catch((err) => {
          toast.error(failureMessage)
          this.saveUserActivityLog(overwrittenConfigurationData, err.status)
          this.revertToLastSavedLanguages()
          throw err
        })
    } else {
      // The copy on develop named the https/mailto rule for every failure, so an
      // admin blocked by braces was told to check a prefix that was already correct.
      // Three modes are reachable now, and one string cannot be right for all three.
      //
      // Modes 1 and 2 ship T1's PO-decided string unchanged. It embeds three
      // corrections from a 474-string audit: say "variables" (the noun `merge tag`
      // appears zero times in dashboard/src), avoid "must" (the register of the
      // inline field-error layer, not of toasts), and do not claim https-only for
      // static links, because http:// still passes when there is no variable.
      //
      // Mode 3 is deliberately plain and does NOT name the feature. PO decision:
      // defensive-only — an admin whose workspace has the flag off should not learn
      // the capability exists from a save failure.
      toast.error(
        callToActionError === CTA_VALIDATION_ERRORS.FEATURE_DISABLED
          ? "This button can't be saved."
          : 'Oops! Please make sure every active button has a link starting with ' +
              'https:// or mailto:. Links with variables need https://.'
      )
    }
  }

  submit = (event) => {
    event.preventDefault()
    this.updateSurveyConfiguration({ patchingConfigurationData: {}, onFailure: () => {} })
  }

  render() {
    const contextValue = {
      ...this.state,
      onCallToActionSettingsChange: this.onCallToActionSettingsChange,
      submitAction: this.updateSurveyConfiguration,
      submitLanguageAction: this.updateSurveyLanguages,
      surveyProps: this.props.survey,
    }

    const surveyLanguages = this.props.surveyLanguages.data
    const surveyLanguage =
      surveyLanguages &&
      surveyLanguages.results.find((surveyLanguage) => surveyLanguage.id === this.state.languageId)

    const surveyType = this.props.survey.data.type

    return (
      <SurveyConfigurationContext.Provider value={contextValue}>
        <div className="grid ui stackable">
          <div className="twelve wide column">
            <h2>Name</h2>
            <form className="ui form">
              <input type="text" value={this.state.surveyName} onChange={this.handleSurveyName} />
            </form>
            <h2>
              <TooltipLabel
                label="Language"
                descriptionTitle="Translating surveys to other languages"
                descriptionContent="Choose a language to translate survey elements, such as the skip and back buttons."
                learnMoreLink="https://help.simplesat.io/en/articles/2696158-translating-surveys-to-other-languages"
              />
            </h2>
            <p>
              Choose the survey language to translate all elements, such as back and skip buttons in
              your web survey.
            </p>
            <SurveyLanguageDropdownContainer
              shouldDisabledLanguageDropdown={this.state.isMultilingualSurveyChecked}
              currentSurveyLangauge={this.props.survey.data.language}
              setLanguage={this.setLanguage}
              surveyLanguages={surveyLanguages}
              languageId={this.state.languageId}
            />
            <SurveyLanguageSetting
              isMultilingualSurveyChecked={this.state.isMultilingualSurveyChecked}
              onChangeIsMultilingualSurveyChecked={this.onChangeIsMultilingualSurveyChecked}
              onChangeSurveyLanguage={this.onChangeSurveyLanguage}
              onChangeDefaultSurveyLanguage={this.onChangeDefaultSurveyLanguage}
              surveyLanguages={this.state.multiSurveyLanguages}
              enabledMultiligualSurvey={this.props.enabledMultiligualSurvey}
            />
            <h2>
              <TooltipLabel
                label="Brand mapping"
                descriptionTitle="Brand mapping"
                descriptionContent="Link your survey to a brand to control design and styling."
                learnMoreLink="https://help.simplesat.io/en/articles/5051375-branding-design-and-customization"
              />
            </h2>
            <p>
              Brand settings dictate the look and feel for a survey. Select which brand to map this
              survey with, or <Link to="/settings/brands/">manage all brands in your account.</Link>
            </p>
            <SurveyBrandDropdownContainer
              currentSurveyBrand={this.props.survey.data.brand}
              onBrandChange={this.onBrandChange}
              surveyBrands={this.props.surveyBrands.data}
            />
            <ExpirationSettings
              settings={this.state.expiration_settings}
              onToggleEnabled={this.onToggleEnabled}
              onChangeDuration={this.onChangeDuration}
              onChangeUnit={this.onChangeUnit}
              timeUntilExpiration={this.state.timeUntilExpiration}
              onChangeTimeUntilExpiration={(value) => {
                this.setState({ timeUntilExpiration: value })
                this.setIsSurveyBuilderDirty(true)
              }}
              expirationUnit={this.state.expirationUnit}
              onChangeExpirationUnit={(value) => {
                this.setState({ expirationUnit: value })
                this.setIsSurveyBuilderDirty(true)
              }}
              isExpirationEnabled={this.state.isExpirationEnabled}
              onChangeIsExpirationEnabled={(checked) => {
                this.setState({ isExpirationEnabled: checked })
                this.setIsSurveyBuilderDirty(true)
              }}
              canSetPostSurveyEmailExpiration={constants.emailSurveyTypes.includes(surveyType)}
              isClosedEnabled={this.state.isClosedEnabled}
              onChangeIsClosedEnabled={this.onChangeIsClosedEnabled}
              scheduleDate={this.state.scheduleDate}
              setScheduleDate={(date) => {
                this.setState({ scheduleDate: date })
                this.setIsSurveyBuilderDirty(true)
              }}
              scheduleTime={this.state.scheduleTime}
              setScheduleTime={(time) => {
                this.setState({ scheduleTime: time })
                this.setIsSurveyBuilderDirty(true)
              }}
            />
            {['generic_embed', 'web_embed'].includes(surveyType) && (
              <React.Fragment>
                <h2>
                  <TooltipLabel
                    label="Capture customer information from anonymous surveys"
                    descriptionTitle="Customer information question"
                    descriptionContent="This is a special question type designed to help you connect a customer and their email address with responses."
                    learnMoreLink="https://help.simplesat.io/en/articles/2776705-survey-questions#customer-information-question"
                  />
                </h2>
                <Form>
                  <Form.Field className="mb2">
                    <Checkbox
                      label="Show question if customer info isn't already associated with the answer"
                      checked={this.state.onlyShowCustomerQuestionIfCustomerUnknown}
                      onChange={(_, { checked }) => {
                        this.setState({ onlyShowCustomerQuestionIfCustomerUnknown: checked })
                        this.setIsSurveyBuilderDirty(true)

                        if (checked === false) {
                          this.setState({
                            isCustomerQuestionRequireAndHideAnonymousResponse: false,
                          })
                        }
                      }}
                    />
                  </Form.Field>
                  <Form.Field className="sub-menu-indent">
                    <Checkbox
                      label="Require the question and hide all anonymous responses"
                      checked={this.state.isCustomerQuestionRequireAndHideAnonymousResponse}
                      disabled={!this.state.onlyShowCustomerQuestionIfCustomerUnknown}
                      onChange={(_, { checked }) => {
                        this.setState({
                          isCustomerQuestionRequireAndHideAnonymousResponse: checked,
                        })
                        this.setIsSurveyBuilderDirty(true)
                      }}
                    />
                  </Form.Field>
                  {this.props.enableFileUploadQuestion && (
                    <Form.Field className="mb2">
                      <Checkbox
                        label="Allow file uploads from anonymous respondents"
                        checked={this.state.isAnonymousFileUploadAllowed}
                        onChange={(_, { checked }) => {
                          this.setState({ isAnonymousFileUploadAllowed: checked })
                          this.setIsSurveyBuilderDirty(true)
                        }}
                        data-testid="survey-file-upload-toggle"
                      />
                    </Form.Field>
                  )}
                </Form>
              </React.Fragment>
            )}
            <h2>
              <TooltipLabel
                label="Open tickets directly in your helpdesk"
                descriptionTitle="Open tickets directly in your helpdesk "
                descriptionContent={
                  <React.Fragment>
                    <p>
                      When enabled, ticket IDs in Simplesat will become links that open the
                      corresponding ticket in your helpdesk.
                    </p>
                    <p>
                      Enter the URL format for individual tickets in your helpdesk, replacing the
                      ticket ID with <code>{'{{ ticket_id }}'}</code>.
                    </p>
                  </React.Fragment>
                }
                learnMoreLink="https://help.simplesat.io/en/articles/3629132-open-tickets-directly-in-your-helpdesk"
              />
            </h2>
            <Form>
              <Form.Field error={Boolean(this.state.ticketUrlPrefixError)}>
                <label>Ticket URL</label>
                <input
                  ref={this.ticketUrlInputRef}
                  type="text"
                  placeholder="https://yourcompany.helpdesk.com/tickets/{{ ticket_id }}"
                  value={this.state.ticketUrlPrefix}
                  onChange={this.handleTicketUrlPrefix}
                />
                {this.state.ticketUrlPrefixError && (
                  <ErrorText>{this.state.ticketUrlPrefixError}</ErrorText>
                )}
              </Form.Field>
            </Form>
          </div>
        </div>
        <h2>
          <TooltipLabel
            label="Confirmation page"
            descriptionTitle="Confirmation page"
            descriptionContent={
              <React.Fragment>
                <p>
                  A final confirmation page will be displayed to your users after they complete a
                  survey.
                </p>
                <p>
                  The page content can be customized based on the sentiment from the survey’s first
                  question.
                </p>
                <p>
                  You can also include call to action buttons to suggest your customers to leave
                  positive reviews.
                </p>
              </React.Fragment>
            }
            learnMoreLink="https://help.simplesat.io/en/articles/7171943-survey-settings#confirmation-page"
          />
        </h2>
        <form className="ui form" onSubmit={this.submit}>
          <div className="grid ui stackable">
            <div className="wide column">
              <Tab
                menu={{ className: 'stackable', attached: 'top', tabular: true }}
                panes={confirmationMessagePanes(
                  this.state.confirmationMessage,
                  this.handleConfirmationMessageChange,
                  surveyLanguage
                )}
                defaultActiveIndex={0}
                onTabChange={this.handleConfirmationMessageTabChange}
              />
            </div>
          </div>
          <button hidden ref={this.submitButtonRef} />
        </form>
      </SurveyConfigurationContext.Provider>
    )
  }
}

export const SurveyConfigurationPresentation = connect()(
  R.compose(
    withRouter,
    withSurveySettingContext,
    connectApi({
      name: 'createUserActivityLog',
      url: `/user-activity-log/write/`,
      method: 'post',
      skip: true,
    }),
    connectApi({
      url: `brands/brands/`,
      name: 'surveyBrands',
    }),
    connectApi({
      url: `survey/languages/`,
      name: 'surveyLanguages',
    }),
    connectApi({
      method: 'patch',
      name: 'updateSurvey',
      skip: true,
      url: (props) => `account/survey/${props.match.params.surveyId}/`,
    })
  )(SurveyConfigurationContainer)
)



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/SurveyConfiguration/index.js